// src/pages/account/MyForms.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Button,
  Tabs,
  Tab,
  Box,
  Chip,
  IconButton,
  Badge,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';
import {
  getFormulariosUsuario,
  getFormularioPendiente,
  getFormulario,
  getAsignaciones,
  listFormularios,
  getRespuestasIndividuales,
  getEvaluacionesPendientes,
  getHistorialEvaluacionesUsuario,
  type Formulario,
  type FormularioPendiente,
  type EvaluacionPendiente,
  type HistorialEvaluacion,
} from '../../api/formularios';
import FormularioRespuestaDialog from '../../components/formularios/FormularioRespuestaDialog';
import EvaluacionConTiempoDialog from '../../components/formularios/EvaluacionConTiempoDialog';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerRespuestasDialog from '../../components/formularios/VerRespuestasDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`forms-tabpanel-${index}`}
      aria-labelledby={`forms-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function MyForms() {
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tabValue, setTabValue] = useState(0); // 0 = Formularios, 1 = Evaluaciones
  const [loadingFormularios, setLoadingFormularios] = useState(false);
  const [loadingEvaluaciones, setLoadingEvaluaciones] = useState(false);
  const [formularioPendiente, setFormularioPendiente] = useState<FormularioPendiente | null>(null);
  const [formularioPendienteCompleto, setFormularioPendienteCompleto] = useState<Formulario | null>(null);
  const [fechaAsignacion, setFechaAsignacion] = useState<string | null>(null);
  const [formulariosCompletados, setFormulariosCompletados] = useState<Formulario[]>([]);
  const [evaluacionesPendientes, setEvaluacionesPendientes] = useState<EvaluacionPendiente[]>([]);
  const [evaluacionesHistorial, setEvaluacionesHistorial] = useState<HistorialEvaluacion[]>([]);
  const [dialogFormularioOpen, setDialogFormularioOpen] = useState(false);
  const [dialogEvaluacionOpen, setDialogEvaluacionOpen] = useState(false);
  const [evaluacionPendiente, setEvaluacionPendiente] = useState<FormularioPendiente | null>(null);
  // Estado para ver respuestas de formularios completados
  const [verRespuestasOpen, setVerRespuestasOpen] = useState(false);
  const [selectedFormularioId, setSelectedFormularioId] = useState<number | null>(null);

  const loadFormularios = useCallback(async () => {
    if (!user?.id) return;
    setLoadingFormularios(true);
    try {
      // Obtener formulario pendiente del usuario actual (solo formularios, no evaluaciones)
      const pendiente = await getFormularioPendiente();
      // Verificar que no sea una evaluación
      if (pendiente) {
        try {
          const formularioCompleto = await getFormulario(pendiente.id);
          // Solo establecer si no es una evaluación
          if (!formularioCompleto.esEvaluacion) {
            setFormularioPendiente(pendiente);
            setFormularioPendienteCompleto(formularioCompleto);

            // Obtener fecha de asignación desde las asignaciones
            const asignaciones = await getAsignaciones(pendiente.id);
            const asignacionUsuario = asignaciones.find((a: any) => a.usuarioId === user.id);
            if (asignacionUsuario?.asignadoAt) {
              setFechaAsignacion(asignacionUsuario.asignadoAt);
            } else if (asignacionUsuario?.createdAt) {
              setFechaAsignacion(asignacionUsuario.createdAt);
            } else {
              setFechaAsignacion(null);
            }
          } else {
            // Es una evaluación, no la mostramos en formularios
            setFormularioPendiente(null);
            setFormularioPendienteCompleto(null);
            setFechaAsignacion(null);
          }
        } catch (error) {
          console.error('Error al cargar formulario completo:', error);
          setFormularioPendiente(null);
          setFormularioPendienteCompleto(null);
          setFechaAsignacion(null);
        }
      } else {
        setFormularioPendiente(null);
        setFormularioPendienteCompleto(null);
        setFechaAsignacion(null);
      }

      // Obtener formularios completados del usuario
      // Necesitamos obtener todos los formularios y luego filtrar los que el usuario ha completado
      // Para esto, obtenemos todos los formularios y luego verificamos las asignaciones y respuestas
      try {
        const { data: todosFormularios } = await listFormularios({ limit: 1000 });
        const formulariosNoEvaluacion = (todosFormularios || []).filter((f: any) => !f.esEvaluacion);
        
        // Para cada formulario, verificar si el usuario tiene una respuesta completada
        const completadosConFecha: Array<Formulario & { completadoAt?: string }> = [];
        
        await Promise.all(
          formulariosNoEvaluacion.map(async (formulario: any) => {
            try {
              // Obtener asignaciones del formulario
              const asignaciones = await getAsignaciones(formulario.id);
              const asignacionUsuario = asignaciones.find((a: any) => a.usuarioId === user.id);
              
              if (asignacionUsuario) {
                // Obtener respuestas del usuario para este formulario
                try {
                  const respuestasData = await getRespuestasIndividuales(formulario.id, {
                    usuarioId: user.id,
                  });
                  
                  const respuestas = respuestasData?.data || [];
                  
                  // Si hay respuestas, el formulario está completado
                  if (respuestas && respuestas.length > 0) {
                    // Obtener la fecha de completado de la primera respuesta (o la más reciente)
                    const respuestaMasReciente = respuestas.sort(
                      (a: any, b: any) => 
                        new Date(b.completadoAt || b.createdAt || 0).getTime() - 
                        new Date(a.completadoAt || a.createdAt || 0).getTime()
                    )[0];
                    
                    const formularioCompleto = await getFormulario(formulario.id);
                    completadosConFecha.push({
                      ...formularioCompleto,
                      completadoAt: respuestaMasReciente?.completadoAt || respuestaMasReciente?.createdAt,
                    });
                  }
                } catch (error) {
                  // Si no hay respuestas, simplemente no agregamos este formulario
                }
              }
            } catch (error) {
              // Si hay error al obtener asignaciones, continuar con el siguiente
            }
          })
        );
        
        setFormulariosCompletados(completadosConFecha);
      } catch (error) {
        console.error('Error al cargar formularios completados:', error);
        setFormulariosCompletados([]);
      }
    } catch (error: any) {
      enqueueSnackbar('Error al cargar formularios', { variant: 'error' });
    } finally {
      setLoadingFormularios(false);
    }
  }, [user?.id, enqueueSnackbar]);

  const loadEvaluaciones = useCallback(async () => {
    if (!user?.id) return;
    setLoadingEvaluaciones(true);
    try {
      const [pendientes, historial] = await Promise.all([
        getEvaluacionesPendientes(),
        getHistorialEvaluacionesUsuario(user.id),
      ]);
      // Filtrar evaluaciones pendientes del usuario actual
      const pendientesUsuario = pendientes.filter((e) => e.usuarioId === user.id);
      setEvaluacionesPendientes(pendientesUsuario || []);
      setEvaluacionesHistorial(historial || []);
    } catch (error: any) {
      enqueueSnackbar('Error al cargar evaluaciones', { variant: 'error' });
    } finally {
      setLoadingEvaluaciones(false);
    }
  }, [user?.id, enqueueSnackbar]);

  // Cargar ambos al montar el componente para que los badges siempre estén visibles
  useEffect(() => {
    loadFormularios();
    loadEvaluaciones();
  }, [loadFormularios, loadEvaluaciones]);

  // Recargar cuando cambia la pestaña para actualizar el contenido visible
  useEffect(() => {
    if (tabValue === 0) {
      loadFormularios();
    } else {
      loadEvaluaciones();
    }
  }, [tabValue, loadFormularios, loadEvaluaciones]);

  // Mapeo de departamentos (igual que en FormulariosPage)
  const getDepartamentoLabel = (dept: string | null | undefined): string => {
    if (!dept) return '-';
    const labels: Record<string, string> = {
      hr: 'HR',
      att_familias: 'Atención Familias',
      att_alumnado: 'Atención Alumnado',
      att_profesorado: 'Atención Profesorado',
      facturacion: 'Facturación',
      admisiones: 'Admisiones',
    };
    return labels[dept] || dept;
  };

  // Formularios pendientes - usar el formulario pendiente obtenido
  const formulariosPendientes = useMemo(() => {
    if (!formularioPendiente) return [];
    // Convertir FormularioPendiente a formato de tabla
    return [
      {
        id: formularioPendiente.id,
        titulo: formularioPendiente.titulo,
        descripcion: formularioPendiente.descripcion,
        fechaLimite: formularioPendiente.fechaLimite,
        createdAt: undefined,
        creadoPorUsuario: undefined,
        departamento: undefined,
      },
    ];
  }, [formularioPendiente]);

  // Separar evaluaciones en pendientes y completadas
  const evaluacionesCompletadas = useMemo(() => {
    return evaluacionesHistorial.filter((e) => e.estado === 'completado');
  }, [evaluacionesHistorial]);

  const handleCompletarFormulario = () => {
    if (formularioPendiente) {
      setDialogFormularioOpen(true);
    }
  };

  const handleFormularioComplete = () => {
    setDialogFormularioOpen(false);
    // Recargar formularios para actualizar la lista
    loadFormularios();
  };

  const handleFormularioClose = () => {
    setDialogFormularioOpen(false);
  };

  const handleCompletarEvaluacion = async (evaluacionId: number) => {
    try {
      // Obtener el formulario completo de la evaluación
      const evaluacionCompleta = await getFormulario(evaluacionId);
      // Convertir a FormularioPendiente para el diálogo
      const evaluacionPendienteData: FormularioPendiente = {
        id: evaluacionCompleta.id,
        titulo: evaluacionCompleta.titulo,
        descripcion: evaluacionCompleta.descripcion,
        fechaLimite: evaluacionCompleta.fechaLimite,
        tiempoLimite: evaluacionCompleta.tiempoLimite,
        preguntas: evaluacionCompleta.preguntas || [],
      };
      setEvaluacionPendiente(evaluacionPendienteData);
      setDialogEvaluacionOpen(true);
    } catch (error: any) {
      enqueueSnackbar('Error al cargar la evaluación', { variant: 'error' });
    }
  };

  const handleEvaluacionComplete = () => {
    setDialogEvaluacionOpen(false);
    setEvaluacionPendiente(null);
    // Recargar evaluaciones para actualizar la lista
    loadEvaluaciones();
  };

  const handleEvaluacionClose = () => {
    setDialogEvaluacionOpen(false);
    setEvaluacionPendiente(null);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'activo':
        return 'success';
      case 'borrador':
        return 'default';
      case 'desactivado':
        return 'warning';
      case 'cerrado':
        return 'info';
      case 'completado':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        Mis formularios y evaluaciones
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>FORMULARIOS</span>
                {formularioPendiente && (
                  <Box
                    sx={{
                      backgroundColor: '#ff4d4f',
                      color: '#fff',
                      borderRadius: '50%',
                      minWidth: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      padding: '0 6px',
                    }}
                  >
                    1
                  </Box>
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>EVALUACIONES</span>
                {evaluacionesPendientes.length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: '#ff4d4f',
                      color: '#fff',
                      borderRadius: '50%',
                      minWidth: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      padding: '0 6px',
                    }}
                  >
                    {evaluacionesPendientes.length}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Formularios */}
      <TabPanel value={tabValue} index={0}>
        <Stack spacing={3}>
          {/* Sección Pendientes */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Pendientes
            </Typography>
            <Paper
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              {loadingFormularios ? (
                <Stack alignItems="center" py={4}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main', color: '#fff' }}>
                      <TableCell sx={{ color: '#fff' }}>Nombre</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Recibido</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Creado por</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Departamento</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Proceso</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Fecha límite</TableCell>
                      <TableCell sx={{ color: '#fff' }} align="center">
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formulariosPendientes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No hay formularios pendientes
                        </TableCell>
                      </TableRow>
                    ) : (
                      formulariosPendientes.map((formulario) => (
                        <TableRow
                          key={formulario.id}
                          sx={{
                            backgroundColor: '#fff',
                            '&:hover': {
                              backgroundColor: '#fff !important',
                            },
                          }}
                        >
                          <TableCell>{formulario.titulo}</TableCell>
                          <TableCell>
                            {fechaAsignacion
                              ? dayjs(fechaAsignacion).format('DD/MM/YYYY')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {formularioPendienteCompleto?.creadoPorUsuario?.nombreCompleto || '-'}
                          </TableCell>
                          <TableCell>
                            {getDepartamentoLabel(formularioPendienteCompleto?.departamento)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Pendiente"
                              size="small"
                              sx={{
                                backgroundColor: '#FF9148',
                                color: 'white',
                                fontWeight: 'medium',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {formulario.fechaLimite
                              ? dayjs(formulario.fechaLimite).format('DD/MM/YYYY')
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleCompletarFormulario}
                            >
                              COMPLETAR
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>

          {/* Sección Completados */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Completados
            </Typography>
            <Paper
              sx={{
                border: '1px solid #000',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              {loadingFormularios ? (
                <Stack alignItems="center" py={4}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#000', color: '#fff' }}>
                      <TableCell sx={{ color: '#fff' }}>Título</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Recibido</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Creado por</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Departamento</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Proceso</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Completado en</TableCell>
                      <TableCell sx={{ color: '#fff' }} align="center">
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formulariosCompletados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No hay formularios completados
                        </TableCell>
                      </TableRow>
                    ) : (
                      formulariosCompletados.map((formulario) => (
                        <TableRow key={formulario.id}>
                          <TableCell>{formulario.titulo}</TableCell>
                          <TableCell>
                            {formulario.createdAt
                              ? dayjs(formulario.createdAt).format('DD/MM/YYYY')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {formulario.creadoPorUsuario?.nombreCompleto || '-'}
                          </TableCell>
                          <TableCell>
                            {getDepartamentoLabel(formulario.departamento)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Completado"
                              size="small"
                              sx={{
                                backgroundColor: '#44A623',
                                color: 'white',
                                fontWeight: 'medium',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {(formulario as any).completadoAt
                              ? dayjs((formulario as any).completadoAt).format('DD/MM/YYYY')
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              title="Ver respuestas"
                              onClick={() => {
                                setSelectedFormularioId(formulario.id);
                                setVerRespuestasOpen(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        </Stack>
      </TabPanel>

      {/* Tab Evaluaciones */}
      <TabPanel value={tabValue} index={1}>
        <Stack spacing={3}>
          {/* Sección Pendientes */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Pendientes
            </Typography>
            <Paper
              sx={{
                border: '1px solid #21a598',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              {loadingEvaluaciones ? (
                <Stack alignItems="center" py={4}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main', color: '#fff' }}>
                      <TableCell sx={{ color: '#fff' }}>Título</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Recibido</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Creado por</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Departamento</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Proceso</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Fecha límite</TableCell>
                      <TableCell sx={{ color: '#fff' }} align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluacionesPendientes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No hay evaluaciones pendientes
                        </TableCell>
                      </TableRow>
                    ) : (
                      evaluacionesPendientes.map((evaluacion) => (
                        <TableRow
                          key={evaluacion.formularioId}
                          sx={{
                            backgroundColor: '#fff',
                            '&:hover': { backgroundColor: '#fff !important' },
                          }}
                        >
                          <TableCell>{evaluacion.formularioTitulo}</TableCell>
                          <TableCell>
                            {dayjs(evaluacion.asignadoAt).format('DD/MM/YYYY')}
                          </TableCell>
                          <TableCell>{evaluacion.usuarioNombre || '-'}</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Chip
                              label="Pendiente"
                              size="small"
                              sx={{
                                backgroundColor: '#FF9148',
                                color: 'white',
                                fontWeight: 'medium',
                              }}
                            />
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() => handleCompletarEvaluacion(evaluacion.formularioId)}
                            >
                              COMPLETAR
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>

          {/* Sección Completados */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Completados
            </Typography>
            <Paper
              sx={{
                border: '1px solid #000',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              {loadingEvaluaciones ? (
                <Stack alignItems="center" py={4}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#000', color: '#fff' }}>
                      <TableCell sx={{ color: '#fff' }}>Título</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Fecha</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Creado por</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Departamento</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Proceso</TableCell>
                      <TableCell sx={{ color: '#fff' }}>Completado en</TableCell>
                      <TableCell sx={{ color: '#fff' }} align="center">
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluacionesCompletadas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No hay evaluaciones completadas
                        </TableCell>
                      </TableRow>
                    ) : (
                      evaluacionesCompletadas.map((evaluacion) => (
                        <TableRow key={evaluacion.id}>
                          <TableCell>{evaluacion.titulo}</TableCell>
                          <TableCell>
                            {dayjs(evaluacion.asignadoAt).format('DD/MM/YYYY')}
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Chip
                              label="Completado"
                              size="small"
                              sx={{
                                backgroundColor: '#44A623',
                                color: 'white',
                                fontWeight: 'medium',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {evaluacion.completados && evaluacion.completados.length > 0
                              ? dayjs(
                                  evaluacion.completados[evaluacion.completados.length - 1]
                                    .completadoAt,
                                ).format('DD/MM/YYYY')
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              title="Ver respuestas"
                              onClick={() => {
                                setSelectedFormularioId(evaluacion.id);
                                setVerRespuestasOpen(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        </Stack>
      </TabPanel>

      {/* Dialog para completar formulario */}
      {formularioPendiente && dialogFormularioOpen && (
        <FormularioRespuestaDialog
          open={dialogFormularioOpen}
          formulario={formularioPendiente}
          onClose={handleFormularioClose}
          onComplete={handleFormularioComplete}
        />
      )}

      {/* Dialog para ver respuestas de formularios completados */}
      <VerRespuestasDialog
        open={verRespuestasOpen}
        formularioId={selectedFormularioId || undefined}
        usuarioId={user?.id}
        onClose={() => {
          setVerRespuestasOpen(false);
          setSelectedFormularioId(null);
        }}
      />

      {/* Dialog para completar evaluación */}
      {evaluacionPendiente &&
        (evaluacionPendiente.tiempoLimite ? (
          <EvaluacionConTiempoDialog
            open={dialogEvaluacionOpen}
            formulario={evaluacionPendiente}
            onClose={handleEvaluacionClose}
            onComplete={handleEvaluacionComplete}
            autoCloseOnResult
          />
        ) : (
          <FormularioRespuestaDialog
            open={dialogEvaluacionOpen}
            formulario={evaluacionPendiente}
            onClose={handleEvaluacionClose}
            onComplete={handleEvaluacionComplete}
            autoCloseOnResult
            autoSubmitOnCancel
          />
        ))}
    </Stack>
  );
}

